import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { announcementsApi, getImageUrl } from "../api";
import ImageLoader from "./common/ImageLoader";
import "./AdminAnnouncements.css";

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    shortDescription: "",
    fullDescription: "",
    date: "",
    time: "",
    location: "",
    organizedBy: "DIET ACM Chapter",
    registrationLink: "",
  });
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await announcementsApi.getAll();
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error("Error fetching announcements:", err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPoster(file);
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || (!poster && !editingId)) {
      return alert("Please fill title and upload a poster.");
    }

    setLoading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    if (poster) data.append("poster", poster);

    try {
      if (editingId) {
        await announcementsApi.update(editingId, data);
        alert("✅ Announcement updated successfully!");
      } else {
        await announcementsApi.create(data);
        alert("✅ Announcement created successfully!");
      }
      setFormData({
        title: "",
        shortDescription: "",
        fullDescription: "",
        date: "",
        time: "",
        location: "",
        organizedBy: "DIET ACM Chapter",
        registrationLink: "",
      });
      setPoster(null);
      setPosterPreview(null);
      setEditingId(null);
      fetchAnnouncements();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement permanently?")) return;
    try {
      await announcementsApi.delete(id);
      alert("🗑️ Deleted successfully!");
      fetchAnnouncements();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return (
    <div className="admin-announcement-section">
      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="fas fa-bullhorn title-icon"></i>
          <div>
            <h2>Manage Announcements</h2>
            <p className="panel-subtitle">Create and broadcast event posters, countdowns, and registration details</p>
          </div>
        </div>
      </div>

      <form className="announcement-form" onSubmit={handleSubmit}>
        <h3 className="form-inner-title">
          <i className="fas fa-plus-circle"></i> {editingId ? "Edit Announcement" : "Create New Announcement"}
        </h3>

        <div className="form-fields-grid">
          <div className="admin-form-group span-2">
            <label>Announcement Title *</label>
            <input 
              type="text" 
              name="title" 
              placeholder="e.g. ACM Web Dev Bootcamp 2026" 
              value={formData.title} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="admin-form-group span-2">
            <label>Short Description / Tagline *</label>
            <textarea
              name="shortDescription"
              placeholder="Brief summary for cards and notifications..."
              value={formData.shortDescription}
              onChange={handleChange}
              rows="2"
              required
            />
          </div>

          <div className="admin-form-group span-2">
            <label>Full Description (Detailed Overview)</label>
            <textarea
              name="fullDescription"
              placeholder="Detailed schedule, speakers, requirements, etc."
              value={formData.fullDescription}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="admin-form-group">
            <label><i className="far fa-calendar-alt"></i> Event Date</label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} />
          </div>

          <div className="admin-form-group">
            <label><i className="far fa-clock"></i> Event Time</label>
            <input type="time" name="time" value={formData.time} onChange={handleChange} />
          </div>

          <div className="admin-form-group">
            <label><i className="fas fa-map-marker-alt"></i> Venue / Location</label>
            <input 
              type="text" 
              name="location" 
              placeholder="e.g. Seminar Hall / Online" 
              value={formData.location} 
              onChange={handleChange} 
            />
          </div>

          <div className="admin-form-group">
            <label><i className="fas fa-users-cog"></i> Organized By</label>
            <input
              type="text"
              name="organizedBy"
              placeholder="DIET ACM Chapter"
              value={formData.organizedBy}
              onChange={handleChange}
            />
          </div>

          <div className="admin-form-group span-2">
            <label><i className="fas fa-link"></i> Registration Link (Google Form / Portal)</label>
            <input
              type="url"
              name="registrationLink"
              placeholder="https://forms.gle/..."
              value={formData.registrationLink}
              onChange={handleChange}
            />
          </div>

          <div className="admin-form-group span-2">
            <label><i className="fas fa-file-image"></i> Event Poster Image *</label>
            <input type="file" accept="image/*" onChange={handleFileChange} />
            {posterPreview && (
              <div className="poster-upload-preview">
                <img src={posterPreview} alt="Preview" />
              </div>
            )}
          </div>
        </div>

        <button type="submit" className="admin-btn-primary submit-announcement-btn" disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i> Processing...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane"></i> {editingId ? "Update Announcement" : "Publish Announcement"}
            </>
          )}
        </button>
      </form>

      {/* Announcements Grid */}
      <div className="announcement-section-display">
        <h3 className="section-feed-title">
          <i className="fas fa-stream"></i> Active Announcements ({announcements.length})
        </h3>

        {announcements.length === 0 ? (
          <p className="no-announcements-text">No announcements published yet.</p>
        ) : (
          <div className="announcement-grid">
            {announcements.map((a) => (
              <div className="announcement-card" key={a.id}>
                <div className="card-media-box">
                  <ImageLoader src={getImageUrl(a.poster)} alt={a.title} />
                  {a.date && (
                    <span className="card-date-chip">
                      <i className="far fa-calendar-alt"></i> {a.date}
                    </span>
                  )}
                </div>

                <div className="announcement-info">
                  <h3>{a.title}</h3>
                  <p className="short-desc">{a.shortDescription}</p>
                  
                  {a.location && (
                    <div className="meta-row">
                      <i className="fas fa-map-marker-alt"></i> {a.location}
                    </div>
                  )}

                  <div className="announcement-actions">
                    <Link to={`/announcements/${a.id}`} className="view-detail-btn">
                      <i className="fas fa-external-link-alt"></i> View Page
                    </Link>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(a.id)}
                      title="Delete this announcement"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;